import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import TypeAhead from '../../components/TypeAhead';
import { regionTone } from '../../utils/marketTone';
import Toggle from '../../components/Toggle';
import MarketConditionsDrawer from '../scheduling/components/MarketConditionsDrawer';
import DatedMarketConditionsDrawer from './DatedMarketConditionsDrawer';
import { marketConditionDot, marketConditionNote, marketConditionTone, marketStatusLabel, type MarketConditionStatus } from '../../utils/marketConditionStatus';
import { DEFAULT_PLAN, winTheDayService, type Dispatcher, type PlanDispatcher, type WinDayPlan } from '../../services/winTheDay.service';
import { reportError } from '../accounting/reports/reportUtils';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { WarningConfirmModal } from '../../components/ui/WarningConfirmModal';
import { useToast } from '../../contexts/ToastContext';

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());
const CONTROL_HEIGHT = 'h-9';
const INPUT_BASE = 'rounded-md border border-theme-border-primary bg-theme-bg-primary text-sm text-heading';
const input = `${INPUT_BASE} px-2.5 py-1.5 ${CONTROL_HEIGHT}`;
//: Inside a table a 36px control sets the row height, and nine of them
//: push the thresholds off the page. Dense rows get their own.
const cellInput = `${INPUT_BASE} h-7 px-2 py-0`;
const button = `rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 ${CONTROL_HEIGHT}`;
const mdy = (iso: string | null) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${m}/${d}/${y}` : iso;
};
const statusLine = 'block h-4 text-[11px] font-semibold uppercase leading-4 tracking-wide';
const fieldLabel = `${statusLine} text-muted`;
const card = 'rounded-xl border border-theme-border-primary bg-theme-bg-primary p-3 dark:bg-theme-bg-secondary';
const thresholdGreen = 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-600 dark:bg-emerald-950 dark:text-emerald-100';
const thresholdYellow = 'border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-100';
/** Panel tint per market -- the body the market tabs sit on top of. */
const regionPanel: Record<string, string> = {
  Houston: 'border-sky-300 bg-sky-50/70 dark:border-sky-800 dark:bg-sky-950/40',
  DFW: 'border-violet-300 bg-violet-50/70 dark:border-violet-800 dark:bg-violet-950/40',
  Memphis: 'border-orange-300 bg-orange-50/70 dark:border-orange-800 dark:bg-orange-950/40',
  Savannah: 'border-teal-300 bg-teal-50/70 dark:border-teal-800 dark:bg-teal-950/40',
  Charleston: 'border-cyan-300 bg-cyan-50/70 dark:border-cyan-800 dark:bg-cyan-950/40',
  Baltimore: 'border-rose-300 bg-rose-50/70 dark:border-rose-800 dark:bg-rose-950/40',
  Norfolk: 'border-indigo-300 bg-indigo-50/70 dark:border-indigo-800 dark:bg-indigo-950/40',
  Mobile: 'border-amber-300 bg-amber-50/70 dark:border-amber-800 dark:bg-amber-950/40',
};

/** Stored metric key -> the name the reports use for it. */
const metricLabel: Record<string, string> = { trucks: 'Drivers working', demurrage_cpc: 'Demurrage CPC', ontime: 'On Time' };
const metricName = (metric: string) => metricLabel[metric] ?? metric.replace(/_/g, ' ');


function addDays(day: string, amount: number) {
  const date = new Date(`${day}T12:00:00`);
  date.setDate(date.getDate() + amount);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function weekStart(day: string) {
  const weekday = new Date(`${day}T12:00:00`).getDay();
  return addDays(day, -((weekday + 6) % 7));
}

function dayLabel(day: string) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(`${day}T12:00:00`));
}

/**
 * Market tabs tinted by the market's own colour, matching the badges in the
 * dispatcher table below. No condition status here: the week cards underneath
 * carry it per day, and tinting or dotting the tab made them disagree with
 * every other market badge on the page.
 */
function MarketTabs({ markets, selected, onSelect }: {
  markets: string[]; selected: string; onSelect: (market: string) => void;
}) {
  return <div className="flex flex-wrap gap-1 border-b border-theme-border-primary" role="tablist" aria-label="Markets">
    {markets.map(name => <button key={name} type="button" role="tab" aria-selected={selected === name}
      onClick={() => onSelect(name)} title={name}
      className={`rounded-t-md border border-b-0 px-3 py-1.5 text-sm transition ${regionTone[name] ?? 'bg-theme-bg-secondary text-heading'} ${selected === name ? (regionPanel[name] ?? '').split(' ').filter(c => c.startsWith('border-') || c.startsWith('dark:border-')).join(' ') : ''} ${selected === name
        ? 'relative z-10 -mb-px font-bold shadow-sm'
        : 'border-theme-border-primary opacity-55 saturate-50 hover:opacity-90 hover:saturate-100'}`}>
      {name}
    </button>)}
  </div>;
}

type DispatcherRow = Dispatcher & { assignment: PlanDispatcher; goal: number | null };
// Not Assigned. A desk is scored only once someone picks its category, so an
// untouched dispatcher is absent from the plan rather than silently defaulted.
const NOT_ASSIGNED = '';
const defaultAssignment: PlanDispatcher = { category: NOT_ASSIGNED, custom_goal: null };

export default function WinTheDaySettings() {
  const [effective, setEffective] = useState(today());
  //: The date the selected plan is stored under, or '' for a new plan.
  //: `effective` is where it will be saved, so the two differ while re-dating.
  const [selected, setSelected] = useState<string>('');
  //: Set once the page has pointed the selector at the plan in use, so a
  //: deliberate switch to "New plan…" is not undone on the next refetch.
  const [selectionReady, setSelectionReady] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  //: The confirmation on screen, if any. Holds what to run on OK, so the
  //: callers stay a single `ask(...)` instead of each growing its own flag.
  const [pending, setPending] = useState<{
    title: string; message: React.ReactNode; confirmLabel: string;
    variant?: 'primary' | 'danger'; run: () => void;
  } | null>(null);
  const ask = (prompt: Omit<NonNullable<typeof pending>, 'run'>, run: () => void) =>
    setPending({ ...prompt, run });
  //: Which plan is on screen. For an existing plan that is its stored date,
  //: so changing the effective date re-aims the save without pulling a
  //: different plan in under the edits. A new plan seeds from its date.
  const planKey = (saved: { effective_date: string | null }) => saved.effective_date ?? DEFAULT_PLAN;
  const loadKey = selected || effective;
  const settingsQuery = useQuery({ queryKey: ['win-the-day-settings', loadKey], queryFn: () => winTheDayService.settings(loadKey) });
  const [plan, setPlan] = useState<WinDayPlan | null>(null);
  const [savedPlan, setSavedPlan] = useState('');
  const [loadedDate, setLoadedDate] = useState('');
  const [market, setMarket] = useState('Houston');
  const [week, setWeek] = useState(weekStart(today()));
  const [editingDay, setEditingDay] = useState<{ market: string; day: string } | null>(null);
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  //: Source date of a copy in progress, and where it is going.
  const [copyFrom, setCopyFrom] = useState<string | null>(null);
  const [copyTo, setCopyTo] = useState(today());
  const weekQuery = useQuery({ queryKey: ['win-the-day-week', week], queryFn: () => winTheDayService.conditions(week, addDays(week, 6)) });
  // The saved-plan list comes back with the settings, so it refreshes with them.

  useEffect(() => {
    if (settingsQuery.data?.plan && loadedDate !== loadKey) {
      setPlan(structuredClone(settingsQuery.data.plan));
      setSavedPlan(JSON.stringify(settingsQuery.data.plan));
      setLoadedDate(loadKey);
    }
  }, [settingsQuery.data, loadKey, loadedDate]);

  // Open on the plan the report is using rather than on an unsaved new one,
  // which would show "Not saved" before anything had been touched.
  useEffect(() => {
    if (selectionReady) return;
    const data = settingsQuery.data;
    const plans = data?.plans;
    if (!data || !plans) return;
    setSelectionReady(true);
    const inUse = plans.find(saved => saved.current);
    if (!inUse) return;
    const key = planKey(inUse);
    // Pointing the selector at the in-use plan changes the query key, which
    // would fetch the whole settings payload a second time on every page load.
    // When the response in hand is already that plan -- the usual case, since
    // the plan in force today IS the newest one -- seed the new key with it.
    //
    // Dated plans only. A dateless default and a date with no plan in force
    // both report plan_effective_date null, so matching on null seeded the
    // default's cache with the legacy baseline the server falls back to --
    // which carries no dispatcher categories. Every load of a site with no
    // dated plans then showed the default's desks blank.
    if (inUse.effective_date && data.plan_effective_date === inUse.effective_date) {
      queryClient.setQueryData(['win-the-day-settings', key], data);
    }
    setSelected(key);
    if (inUse.effective_date) setEffective(inUse.effective_date);
  }, [settingsQuery.data, selectionReady, queryClient]);

  const settings = settingsQuery.data;
  // Two independent saves, so two dirty flags: the plan covers goals,
  // thresholds and weights; dispatcher assignments save on their own button.
  // Both write the same win_day_plans row -- separate saves, one record.
  const baseline = savedPlan ? JSON.parse(savedPlan) as WinDayPlan : null;
  const planPart = (value: WinDayPlan | null) => value && JSON.stringify({ markets: value.markets, thresholds: value.thresholds, weights: value.weights });
  const dirty = !!plan && !!baseline && planPart(plan) !== planPart(baseline);
  const dispatchersDirty = !!plan && !!baseline && JSON.stringify(plan.dispatchers) !== JSON.stringify(baseline.dispatchers);
  //: Anything the page is holding that the database is not: edits, or an
  //: effective date aimed somewhere other than where the plan is stored.
  const onDefault = selected === DEFAULT_PLAN;
  const reDating = !!selected && !onDefault && effective !== selected;
  const unsaved = dirty || dispatchersDirty || reDating;
  const defaultPlan = onDefault;
  //: Dated plans newest first -- so the current one, which is the newest,
  //: leads -- and the default last, as the floor they all override.
  const planOptions = [...(settings?.plans ?? [])].sort((a, b) =>
    Number(a.is_default) - Number(b.is_default)
    || (b.effective_date ?? '').localeCompare(a.effective_date ?? ''));
  //: Back to what is stored, without a page reload. Both halves, since the
  //: chip covers both.
  const revert = () => {
    if (selected && !onDefault) setEffective(selected);
    if (baseline) setPlan(structuredClone(baseline));
    setNotice('');
  };
  const editPlan = (change: (draft: WinDayPlan) => void) => setPlan(current => {
    if (!current) return current;
    const next = structuredClone(current);
    change(next);
    return next;
  });
  const choosePlan = (value: string) => {
    if (dirty) {
      ask({ title: 'Discard unsaved changes?',
            message: 'Switching plans will discard the changes you have made to this one.',
            confirmLabel: 'Discard and switch', variant: 'danger' },
          () => applyPlanChoice(value));
      return;
    }
    applyPlanChoice(value);
  };
  const applyPlanChoice = (value: string) => {
    setSelected(value);
    setSelectionReady(true);
    setCopyFrom(null);
    setNotice('');
    setEffective(value === DEFAULT_PLAN ? today() : value);
  };
  const changeDate = (day: string) => {
    if (!day || day === effective) return;
    // Editing an existing plan: only the save target moves, the edits stay.
    if (selected) { setEffective(day); setNotice(''); return; }
    if (dirty) {
      ask({ title: 'Discard unsaved changes?',
            message: 'Changing the effective date will load the plan for that date and discard your changes.',
            confirmLabel: 'Discard and change', variant: 'danger' },
          () => applyDateChange(day));
      return;
    }
    applyDateChange(day);
  };
  const applyDateChange = (day: string) => {
    setPlan(null);
    setSavedPlan('');
    setLoadedDate('');
    setEffective(day);
    setNotice('');
  };
  // Both saves rewrite the stored plan for this date, so each re-reads it
  // afterwards rather than trusting the local copy.
  const persist = async (write: () => Promise<unknown>, success: string, failure: string) => {
    if (!plan) return;
    setSaving(true);
    setNotice('');
    try {
      await write();
      const refreshed = await settingsQuery.refetch();
      if (refreshed.data?.plan) {
        setPlan(structuredClone(refreshed.data.plan));
        setSavedPlan(JSON.stringify(refreshed.data.plan));
      }
      showToast({ message: success, type: 'success' });
      return true;
    } catch (error) {
      setNotice(reportError(error, failure));
      return false;
    } finally {
      setSaving(false);
    }
  };
  // The report reads the newest plan, so deleting it hands the board back to
  // the one before -- which is how a mis-dated save is undone.
  const duplicatePlan = async () => {
    if (!copyFrom) return;
    setSaving(true);
    setNotice('');
    try {
      await winTheDayService.copyPlan(copyFrom, copyTo);
      await settingsQuery.refetch();
      setCopyFrom(null);
      setSelected(copyTo);
      setEffective(copyTo);
      showToast({ message: `Plan from ${copyFrom === DEFAULT_PLAN ? 'the default' : mdy(copyFrom)} copied to ${mdy(copyTo)}.`, type: 'success' });
    } catch (error) {
      setNotice(reportError(error, 'Could not copy the plan.'));
    } finally {
      setSaving(false);
    }
  };
  // Dispatcher categories live in the plan row, so they go with it. Worth
  // saying out loud: the two are saved by different buttons, which reads as
  // though they were stored apart.
  const removePlan = (day: string) => ask({
    title: `Delete the plan effective ${mdy(day)}?`,
    message: <>This also removes that plan&rsquo;s <b>dispatcher categories</b>, which are stored
      with it. The report falls back to the next most recent plan, including its dispatcher
      assignments.</>,
    confirmLabel: 'Delete plan', variant: 'danger',
  }, () => void runDeletePlan(day));
  const runDeletePlan = async (day: string) => {
    setSaving(true);
    setNotice('');
    try {
      await winTheDayService.deletePlan(day);
      const refreshed = await settingsQuery.refetch();
      if (refreshed.data?.plan) {
        setPlan(structuredClone(refreshed.data.plan));
        setSavedPlan(JSON.stringify(refreshed.data.plan));
      }
      if (selected === day) { setSelected(''); setEffective(today()); }
      showToast({ message: `Plan for ${mdy(day)} deleted.`, type: 'success' });
    } catch (error) {
      setNotice(reportError(error, 'Could not delete the plan.'));
    } finally {
      setSaving(false);
    }
  };
  const savePlan = async () => {
    const moved = reDating;
    // The default is addressed by the sentinel, not by a date. `effective`
    // still holds one -- it is seeded when the default is picked so the field
    // has something if you switch away -- and sending it saved the default as
    // a dated plan for today.
    const target = onDefault ? DEFAULT_PLAN : effective;
    const ok = await persist(() => winTheDayService.savePlan(target, plan!, selected || undefined),
      onDefault ? 'Default plan saved.'
        : moved ? `Plan moved from ${mdy(selected)} to ${mdy(effective)}.`
        : `Plan saved for ${mdy(effective)}.`,
      'Could not save the Win the Day plan.');
    // Follow the plan: after a move or a create it lives under a new date, and
    // leaving the selector behind would aim the next save at a plan that has
    // gone, or at nothing.
    if (ok && !onDefault) setSelected(effective);
  };
  const setBandEnabled = (name: string, band: string, enabled: boolean) => editPlan(draft => {
    draft.markets[name].bands[band].enabled = enabled;
    if (!enabled) {
      for (const dispatcher of settings?.dispatchers ?? []) {
        if (dispatcher.market === name && draft.dispatchers[dispatcher.username]?.category === band) {
          draft.dispatchers[dispatcher.username] = { ...defaultAssignment };
        }
      }
    }
  });
  const goalFor = (dispatcher: Dispatcher, assignment: PlanDispatcher) => {
    const config = plan?.markets[dispatcher.market];
    if (!config) return null;
    if (assignment.category === NOT_ASSIGNED) return null;
    if (assignment.category === 'CUSTOM') return assignment.custom_goal;
    if (assignment.category === 'MARKET DEFAULT') return config.default_turns_per_truck;
    return config.bands[assignment.category]?.turns_per_truck ?? null;
  };
  const dispatcherRows: DispatcherRow[] = (settings?.dispatchers ?? [])
    .map(d => {
      const assignment = plan?.dispatchers[d.username] ?? defaultAssignment;
      return { ...d, assignment, goal: goalFor(d, assignment) };
    });

  const columns: ColumnDef<DispatcherRow>[] = [
    { accessorKey: 'name', header: 'Dispatcher', size: 190, cell: ({ row }) => <span className="font-medium text-heading">{row.original.name}</span> },
    { accessorKey: 'market', header: 'Market', size: 120, cell: ({ row }) => <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${regionTone[row.original.market] ?? 'bg-theme-bg-secondary text-heading'}`}>{row.original.market}</span> },
    { id: 'category', accessorFn: row => row.assignment.category, header: 'Category', size: 220, cell: ({ row }) => {
      const d = row.original;
      const enabledBands = settings?.bands.filter(band => plan?.markets[d.market]?.bands[band]?.enabled) ?? [];
      return <select aria-label={`${d.name} category`} className={`${cellInput} w-full min-w-44`} value={d.assignment.category} onChange={event => editPlan(draft => {
        const category = event.target.value;
        draft.dispatchers[d.username] = { ...d.assignment, category, custom_goal: category === 'CUSTOM' ? d.assignment.custom_goal : null };
      })}><option value={NOT_ASSIGNED}></option><option value="CUSTOM">Custom</option><option value="MARKET DEFAULT">Market Default</option>{enabledBands.map(band => <option key={band} value={band}>{band}</option>)}</select>;
    } },
    { id: 'goal', accessorFn: row => row.goal ?? '', header: 'Goal / truck / day', size: 180, cell: ({ row }) => {
      const d = row.original;
      return <input aria-label={`${d.name} goal`} className={`${cellInput} w-28 ${d.assignment.category !== 'CUSTOM' ? 'bg-theme-bg-secondary text-muted' : ''}`} type="number" min="0.001" step="0.001" value={d.goal ?? ''} readOnly={d.assignment.category !== 'CUSTOM'} placeholder={d.assignment.category === 'CUSTOM' ? 'Enter goal' : d.assignment.category === NOT_ASSIGNED ? 'Not scored' : 'Not set'} onChange={event => editPlan(draft => {
        draft.dispatchers[d.username] = { ...d.assignment, category: 'CUSTOM', custom_goal: event.target.value === '' ? null : Number(event.target.value) };
      })} />;
    } },
    { id: 'order_dispatchers', accessorFn: row => (row.assignment.order_dispatchers ?? []).join(' '),
      header: 'Order dispatcher', size: 320, cell: ({ row }) => {
      const d = row.original;
      const covered = d.assignment.order_dispatchers ?? [];
      // A lookup, not a list: there are fifty-odd logins and scrolling a
      // native select past them to find one name is the wrong shape. Names
      // lead, since the login is what is stored but not what people know.
      // Chips below, not beside: in a table cell they ran off the edge and
      // pushed the input down to a sliver.
      return <TypeAhead multiple compact showSearch chipsDirection="down"
        ariaLabel={`Order dispatchers for ${d.name}`}
        placeholder="Same as name"
        value={covered}
        options={(settings?.order_dispatchers ?? []).map(option => ({
          value: option.username,
          label: option.name || option.username,
          labelSuffix: option.name ? option.username : undefined,
          // Logins with open orders lead; the rest are active dispatchers
          // surfaced once you type, so a desk whose orders have all closed is
          // still reachable rather than missing from the list.
          searchOnly: !option.on_orders,
        }))}
        onChange={next => editPlan(draft => {
          draft.dispatchers[d.username] = { ...d.assignment, order_dispatchers: next };
        })} />;
    } },
  ];
  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(week, index));

  if (settingsQuery.isError) return <div className="p-6 text-red-700" role="alert">{reportError(settingsQuery.error, 'Could not load settings.')} <button className="underline" onClick={() => void settingsQuery.refetch()}>Retry</button></div>;
  if (settingsQuery.isLoading || !settings || !plan) return <p className="p-6">Loading Win the Day settings…</p>;

  return <main className="space-y-4 p-4 text-heading md:p-5">
    {/* Not sticky: at this height it covered the first section heading as the
        page scrolled, and the plan controls are not needed mid-page. */}
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 rounded-xl border border-theme-border-primary bg-theme-bg-primary p-3 shadow-sm">
      <div><p className="text-xs font-semibold uppercase tracking-widest text-muted">Researching / Reports</p><h1 className="text-2xl font-bold">Win the Day Settings</h1><p className="text-xs text-muted">Goals, thresholds, weights and dispatcher categories are one dated plan. Market conditions save separately.</p></div>
      <div className="flex flex-wrap items-end gap-4">
        {/* Labels above, fields on one baseline, so the controls line up
            instead of each sitting at its own height. */}
        <div className="flex flex-col gap-1">
          <label className={fieldLabel} htmlFor="win-day-plan">Plan</label>
          <select id="win-day-plan" className={input} value={selected}
                  onChange={event => choosePlan(event.target.value)}>
            {planOptions.map(saved => <option key={planKey(saved)} value={planKey(saved)}>
              {saved.is_default ? 'Default' : mdy(saved.effective_date)}{saved.current ? ' (current)' : ''}
            </option>)}
          </select>
        </div>

        {!onDefault && <div className="flex flex-col gap-1">
          <span className={fieldLabel}>Effective date</span>
          <IsoDatePicker ariaLabel="Plan effective date" value={effective} onChange={changeDate} />
        </div>}

        <div className="flex flex-col gap-1">
          {/* One chip for both kinds of unsaved change -- edits and a re-date --
              because to the reader they are the same thing: this is not what is
              stored. Revert is how you get back without reloading the page. */}
          <span className={`${statusLine} text-amber-700 dark:text-amber-400`}
                title={reDating ? `Will move from ${mdy(selected)}` : undefined}>{unsaved ? 'Not saved' : ''}</span>
          <div className="flex items-center gap-2">
            {unsaved && <button type="button" className={input} disabled={saving} onClick={revert}>Revert</button>}
            <button className={button} disabled={saving || !unsaved} onClick={() => void savePlan()}>{saving ? 'Saving…' : 'Save'}</button>
            <span className="mx-1 h-6 w-px bg-theme-border-primary" aria-hidden="true" />
            <button type="button" className={input} disabled={saving}
              onClick={() => { setCopyTo(today()); setCopyFrom(selected); }}>Copy plan</button>
            <button type="button" disabled={saving || defaultPlan}
              title={defaultPlan ? 'The default plan cannot be deleted -- it is what the report falls back to.' : undefined}
              className={`rounded-md border border-theme-border-primary px-3 py-1.5 text-sm font-semibold text-red-700 hover:border-red-300 hover:bg-red-50 disabled:opacity-40 dark:text-red-300 dark:hover:bg-red-950 ${CONTROL_HEIGHT}`}
              onClick={() => void removePlan(selected)}>Delete</button>
          </div>
        </div>
      </div>
    </header>
    {notice && <p role="status" className={`rounded-lg p-2.5 text-sm ${notice.includes('Could not') ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>{notice}</p>}

    {copyFrom && <div className={`${card} flex flex-wrap items-end gap-2`}>
        <p className="w-full text-sm text-label">Copy {copyFrom === DEFAULT_PLAN ? 'the default plan' : mdy(copyFrom)} to</p>
        {/* Labelled and laid out like the header's own date, because it is the
            same thing: the date the plan being written will take effect. */}
        <div className="flex flex-col gap-1">
          <span className={fieldLabel}>Effective date</span>
          <IsoDatePicker ariaLabel="Effective date for the copied plan" value={copyTo} onChange={setCopyTo} />
        </div>
        <button className={button} disabled={saving} onClick={() => void duplicatePlan()}>{saving ? 'Saving…' : 'Save'}</button>
        <button className={input} disabled={saving} onClick={() => setCopyFrom(null)}>Cancel</button>
    </div>}

    {/* One group because it is one record: goals, scoring and dispatcher
        categories are all the selected plan and all save on Save. The week
        grid below stays outside it -- those edits write immediately. */}
    <section className="space-y-3 rounded-xl border-2 border-gray-400 bg-gray-100 p-3 dark:border-gray-500 dark:bg-gray-800" aria-labelledby="plan-settings-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="plan-settings-heading" className="text-base font-bold">Plan settings</h2>
        <p className="text-xs text-muted">Goals, scoring and dispatcher categories all save together on <span className="font-semibold">Save</span>, {onDefault ? <>as the <span className="font-semibold">default</span> &mdash; used whenever no dated plan applies</> : <>effective {mdy(effective)}</>}.</p>
      </div>
    {/* One card: goals, scoring and dispatcher categories are the same plan and
        save on the same button. Goals stack down the left, the dispatcher
        table takes the right, and the whole thing stacks below xl. */}
    <div className="grid gap-3 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] xl:items-start">
    <div className="space-y-3">
    <section className={card}>
      <div className="mb-2"><h2 className="text-base font-bold">Market category goals</h2><p className="text-xs text-muted">Set a market default and enable the mileage bands dispatchers may select.</p></div>
      <MarketTabs markets={settings.markets} selected={market} onSelect={setMarket} />
      <div className={`rounded-lg rounded-tl-none border p-2 ${regionPanel[market] ?? 'border-theme-border-primary bg-theme-bg-secondary'}`}>
      <div className="flex flex-wrap items-center gap-3"><label className="flex items-center gap-2 text-sm font-semibold">Default Turns<input aria-label={`${market} market default turns`} className={`${cellInput} w-28`} type="number" min="0.001" step="0.001" value={plan.markets[market]?.default_turns_per_truck ?? ''} placeholder="Not set" onChange={event => editPlan(draft => { draft.markets[market].default_turns_per_truck = event.target.value === '' ? null : Number(event.target.value); })} /></label><span className="text-xs text-muted">Used when a dispatcher is set to Market Default.</span></div>
      <div className="mt-2 overflow-x-auto"><table className="w-full min-w-[420px] text-sm"><thead className="bg-theme-bg-secondary text-left text-xs uppercase tracking-wide text-muted"><tr><th className="px-3 py-1.5">Use</th><th className="px-3 py-1.5">Mileage category</th><th className="px-3 py-1.5">Turns / truck / day</th></tr></thead><tbody>{settings.bands.map(band => {
        const config = plan.markets[market]?.bands[band];
        return <tr key={band} className={`border-t border-theme-border-primary ${!config?.enabled ? 'opacity-50' : ''}`}><td className="px-3 py-1"><Toggle ariaLabel={`Enable ${band} in ${market}`} checked={!!config?.enabled} onChange={next => setBandEnabled(market, band, next)} /></td><td className="px-3 py-1 font-medium">{band}</td><td className="px-3 py-1"><input aria-label={`${band} goal`} className={`${cellInput} w-24`} type="number" min="0.001" step="0.001" value={config?.turns_per_truck ?? ''} disabled={!config?.enabled} onChange={event => editPlan(draft => { draft.markets[market].bands[band].turns_per_truck = Number(event.target.value); })} /></td></tr>;
      })}</tbody></table></div>
      </div>
    </section>

    {/* One card, not two: the weights filled a fraction of their column and
        left the rest blank. The cutoff headings sit once above the table
        instead of repeating on all eight rows. */}
    <section className={card}>
      <h2 className="text-base font-bold">Scoring</h2>
      <p className="text-xs text-muted">Weights decide how the score is built; thresholds set its green and yellow cutoffs, in percent. Demurrage CPC runs in reverse.</p>
      {/* Two separate settings. Sharing one panel made the prepull box look
          like a third handle on the slider beside it. */}
      <div className="mt-2 flex flex-wrap items-stretch gap-3">
        <div className="min-w-72 flex-1 rounded-lg bg-theme-bg-secondary p-3">
          {/* The weights are the number being dragged, so they are the size of
              a number being dragged -- at 12px the slider looked like it moved
              nothing. */}
          <div className="mb-2 flex items-baseline justify-between gap-4">
            <span className="text-base font-bold tabular-nums text-heading">Turns <span className="text-blue-600 dark:text-blue-400">{Math.round(Number(plan.weights.turn_weight) * 100)}%</span></span>
            <span className="text-base font-bold tabular-nums text-heading">On Time <span className="text-blue-600 dark:text-blue-400">{Math.round(Number(plan.weights.ontime_weight) * 100)}%</span></span>
          </div>
          <input aria-label="Turn weight versus on-time weight" className="w-full accent-blue-600"
            type="range" min="0" max="100" step="5"
            value={Math.round(Number(plan.weights.turn_weight) * 100)}
            onChange={event => editPlan(draft => {
              const turn = Number(event.target.value);
              draft.weights.turn_weight = turn / 100;
              draft.weights.ontime_weight = (100 - turn) / 100;
            })} />
          <p className="mt-1 text-xs text-muted">The two always total 100%.</p>
        </div>
        <label className="grid content-start gap-1 rounded-lg bg-theme-bg-secondary p-3 text-xs font-medium text-muted">Prepull credit %
          <input aria-label="Prepull credit %" className={`${cellInput} w-24`} type="number" min="0" max="100" step="0.1"
            value={Number((Number(plan.weights.prepull_credit) * 100).toFixed(3))}
            onChange={event => editPlan(draft => { draft.weights.prepull_credit = Number(event.target.value) / 100; })} />
          <span className="text-[11px] font-normal">Fraction of a turn an LFD prepull earns.</span>
        </label>
      </div>
      <div className="mt-2 overflow-x-auto"><table className="w-full min-w-[320px] text-sm">
        <thead className="bg-theme-bg-secondary text-left text-xs uppercase tracking-wide text-muted">
          <tr><th className="px-3 py-1.5">Metric</th><th className="px-3 py-1.5">Green %</th><th className="px-3 py-1.5">Yellow %</th></tr>
        </thead>
        <tbody>{Object.entries(plan.thresholds).sort((a, b) => metricName(a[0]).localeCompare(metricName(b[0]))).map(([metric, threshold]) => <tr key={metric} className="border-t border-theme-border-primary">
          <td className="px-3 py-1 font-medium capitalize">{metricName(metric)}</td>
          {(['green', 'yellow'] as const).map(tone => <td key={tone} className="px-3 py-1">
            {/* Tint each cutoff with the band it opens, so the pair reads at a glance. */}
            <input aria-label={`${metricName(metric)} ${tone} cutoff percent`}
              className={`${cellInput} w-20 ${tone === 'green' ? thresholdGreen : thresholdYellow}`}
              type="number" step="0.1" value={Number((threshold[tone] * 100).toFixed(3))}
              onChange={event => editPlan(draft => { draft.thresholds[metric][tone] = Number(event.target.value) / 100; })} />
          </td>)}
        </tr>)}</tbody>
      </table></div>
    </section>
    </div>
      <section className={card}>
        {/* No market dropdown: the table's own column filter already does it. */}
        <div className="mb-2"><h2 className="text-base font-bold">Dispatcher category and goal</h2><p className="text-xs text-muted">Only dispatchers with a category appear on the Win the Day reports. Market Default and enabled bands inherit their goal; choose Custom to enter a different one. <b>Order dispatcher</b> is the login this desk's orders carry, which is where on-time is measured &mdash; leave it blank to use the desk's own name.</p></div>
        <div className="h-[calc(100vh-18rem)] min-h-[32rem]">{/* No virtualization: the rows carry a lookup taller than the 40px the
            virtualizer estimates, and correcting that by measurement staggered
            them over each other. Only desks with trucks are listed, so there
            are tens of rows, not hundreds. */}
          <TanTable<DispatcherRow> data={dispatcherRows} columns={columns} cardTitle={`Dispatchers (${dispatcherRows.length})`} settingsKey="win-the-day-dispatcher-goals-v2" searchPlaceholder="Search dispatchers…" enableSearch enableColumnFilters fillContainer disableVirtualization getRowId={row => row.username} /></div>
      </section>
    </div>
    </section>

    <section className={card}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-lg font-bold">Market conditions by week</h2><p className="text-xs text-muted">Select a day to edit its Scheduler conditions in the market drawer. These save as you edit them, not with the plan.</p></div><div className="flex flex-wrap items-center gap-2"><button className={input} onClick={() => setWeek(addDays(week, -7))} aria-label="Previous week">←</button><IsoDatePicker ariaLabel="Choose week" value={week} onChange={value => setWeek(weekStart(value))} /><button className={input} onClick={() => setWeek(addDays(week, 7))} aria-label="Next week">→</button><button className={input} onClick={() => setWeek(weekStart(today()))}>This week</button></div></div>
      <MarketTabs markets={settings.markets} selected={market} onSelect={setMarket} />
      {weekQuery.isError &&<p role="alert" className="mt-3 text-sm text-red-600">{reportError(weekQuery.error, 'Could not load market conditions.')} <button className="underline" onClick={() => void weekQuery.refetch()}>Retry</button></p>}
      <div className={`rounded-lg rounded-tl-none border p-2 ${regionPanel[market] ?? 'border-theme-border-primary bg-theme-bg-secondary'}`}>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">{weekDays.map(day => {
        const condition = weekQuery.data?.find(row => row.market === market && row.day === day);
        const status = condition?.condition_status ?? 'green';
        const images = condition?.image_count ?? 0;
        return <button key={day} type="button" onClick={() => setEditingDay({ market, day })}
          // A button centres its content vertically when it is taller than the
          // content, so quiet days floated while busy ones filled the card.
          className={`flex min-h-36 flex-col items-stretch justify-start rounded-lg border p-3 text-left transition hover:border-blue-500 hover:shadow-sm ${marketConditionTone[status]}`}>
          <div className="flex items-center justify-between gap-1">
            <h3 className="text-sm font-semibold underline decoration-dotted underline-offset-2">{dayLabel(day)}</h3>
            {/* The dot says the status; the word alongside it was redundant. */}
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${marketConditionDot[status]}`}
              role="img" aria-label={`${marketStatusLabel(status)} status`} title={marketStatusLabel(status)} />
          </div>
          {condition?.days_out != null && <p className="mt-1 text-xs font-medium">{condition.days_out} days out</p>}
          <p className="mt-2 line-clamp-5 whitespace-pre-wrap text-xs">{marketConditionNote(condition?.conditions)}</p>
          {images > 0 && <p className="mt-2 text-xs font-medium" aria-label={`${images} screenshots`}>📷 {images}</p>}
        </button>;
      })}</div>
      </div>
    </section>
    {editingDay && (editingDay.day === today()
      ? <MarketConditionsDrawer isOpen onClose={() => { setEditingDay(null); void weekQuery.refetch(); }} initialMarket={editingDay.market} />
      : <DatedMarketConditionsDrawer day={editingDay.day} market={editingDay.market} markets={settings.markets} onClose={() => setEditingDay(null)} onSaved={() => { void weekQuery.refetch(); }} />)}
    <WarningConfirmModal
      open={!!pending}
      title={pending?.title ?? ''}
      message={pending?.message ?? ''}
      confirmLabel={pending?.confirmLabel ?? 'Confirm'}
      cancelLabel="Cancel"
      confirmVariant={pending?.variant ?? 'primary'}
      onConfirm={() => { const run = pending?.run; setPending(null); run?.(); }}
      onCancel={() => setPending(null)}
    />
  </main>;
}
